/**
 * The shared vocabulary every game engine is built on: a standard 52-card,
 * French-suited deck. This module is pure data and pure helpers — no game
 * rules, no I/O, no rendering.
 */

/** The four French suits, in a canonical, stable order. */
export const SUITS = ["clubs", "diamonds", "hearts", "spades"] as const;
export type Suit = (typeof SUITS)[number];

/**
 * Card ranks are integers so they order and compare naturally. Aces are high
 * (14) by default; games in which the Ace plays low (e.g. Pasur's "sum to 11")
 * express that in their own scoring/capture logic — never by mutating this
 * scale.
 */
export const RANKS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] as const;
export type Rank = (typeof RANKS)[number];

export const RANK_JACK: Rank = 11;
export const RANK_QUEEN: Rank = 12;
export const RANK_KING: Rank = 13;
export const RANK_ACE: Rank = 14;

/** An immutable playing card. */
export interface Card {
  readonly suit: Suit;
  readonly rank: Rank;
}

/** Two-character, stable serialization of a card, e.g. `"AS"`, `"TD"`, `"2C"`. */
export type CardId = string;

const SUIT_CODE: Record<Suit, string> = {
  clubs: "C",
  diamonds: "D",
  hearts: "H",
  spades: "S",
};

const CODE_SUIT: Record<string, Suit> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

export const SUIT_SYMBOL: Record<Suit, string> = {
  clubs: "♣",
  diamonds: "♦",
  hearts: "♥",
  spades: "♠",
};

/** Human-facing rank label: `"2"`…`"10"`, `"J"`, `"Q"`, `"K"`, `"A"`. */
export function rankLabel(rank: Rank): string {
  switch (rank) {
    case 11:
      return "J";
    case 12:
      return "Q";
    case 13:
      return "K";
    case 14:
      return "A";
    default:
      return String(rank);
  }
}

/** Single-character rank code used inside a {@link CardId} (`10` → `"T"`). */
function rankCode(rank: Rank): string {
  return rank === 10 ? "T" : rankLabel(rank);
}

function parseRankCode(ch: string): Rank {
  switch (ch) {
    case "T":
      return 10;
    case "J":
      return 11;
    case "Q":
      return 12;
    case "K":
      return 13;
    case "A":
      return 14;
    default: {
      const n = Number(ch);
      if (Number.isInteger(n) && n >= 2 && n <= 9) return n as Rank;
      throw new Error(`Invalid rank code: "${ch}"`);
    }
  }
}

/** Serialize a card to its {@link CardId}. */
export function cardId(card: Card): CardId {
  return `${rankCode(card.rank)}${SUIT_CODE[card.suit]}`;
}

/** Parse a {@link CardId} back into a card. Throws on malformed input. */
export function parseCardId(id: CardId): Card {
  if (id.length !== 2) throw new Error(`Invalid card id: "${id}"`);
  const rank = parseRankCode(id.charAt(0));
  const suit = CODE_SUIT[id.charAt(1)];
  if (suit === undefined) throw new Error(`Invalid suit code in card id: "${id}"`);
  return { suit, rank };
}

/** Structural equality for cards. */
export function cardsEqual(a: Card, b: Card): boolean {
  return a.rank === b.rank && a.suit === b.suit;
}

/** Compact, human-readable name for logs and errors, e.g. `"A♠"`, `"10♥"`. */
export function cardName(card: Card): string {
  return `${rankLabel(card.rank)}${SUIT_SYMBOL[card.suit]}`;
}
